import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import '../../v2-theme.css';
import './AuthPages.css';
import './RegisterPages.css';

/**
 * Restaurant Registration Page
 * Matches restaurant-register.html exactly
 * Complex form with business info and US license validation
 */
const RestaurantRegister = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const [formData, setFormData] = useState({
    // Business Info
    businessLogo: null,
    businessName: '',
    establishmentType: '',

    // Address
    address: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',

    // Contact
    phone: '',
    licenseNumber: '',
    contactName: '',
    contactTitle: '',
    contactEmail: '',

    // Communication Preferences
    commPreferences: {
      email: false,
      sms: false  // Will be required
    },

    // Account Setup
    password: '',
    confirmPassword: '',
    acceptedTerms: false
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const ESTABLISHMENT_TYPES = [
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'hotel', label: 'Hotel' },
    { value: 'catering', label: 'Catering' },
    { value: 'event-venue', label: 'Event Venue' },
    { value: 'bar', label: 'Bar' },
    { value: 'cafe', label: 'Café' },
    { value: 'other', label: 'Other' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        e.target.value = '';
        return;
      }
      setFormData(prev => ({ ...prev, businessLogo: file }));
    }
  };

  const handleCommPrefChange = (pref) => {
    setFormData(prev => ({
      ...prev,
      commPreferences: {
        ...prev.commPreferences,
        [pref]: !prev.commPreferences[pref]
      }
    }));
  };

  const isUSBased = (country) => {
    const normalized = country.trim().toLowerCase();
    return ['united states', 'usa', 'us', 'united states of america'].includes(normalized);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation: US-based restaurants require license number
    if (isUSBased(formData.country) && !formData.licenseNumber.trim()) {
      setError('Food Service License Number is required for US-based restaurants.');
      return;
    }

    // Validation: Text messaging required
    if (!formData.commPreferences.sms) {
      setError('Text messaging is required to accept shifts.');
      return;
    }

    // Validation: Terms acceptance
    if (!formData.acceptedTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy to register.');
      return;
    }

    // Validation: Password length
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    // Validation: Passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      // Sign up user
      const result = await signUp(formData.contactEmail, formData.password, 'restaurant');

      if (!result.success) {
        setError(result.error || 'Registration failed. Please try again.');
        setLoading(false);
        return;
      }

      // Upload logo if provided
      let logoUrl = null;
      if (formData.businessLogo) {
        const logoFormData = new FormData();
        logoFormData.append('file', formData.businessLogo);
        logoFormData.append('type', 'logo');
        const logoResponse = await api.post('/profiles/upload', logoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        logoUrl = logoResponse.data.url;
      }

      // Create restaurant profile
      await api.post('/profiles/restaurant', {
        business_name: formData.businessName,
        business_logo: logoUrl,
        establishment_type: formData.establishmentType,
        address: formData.address,
        address2: formData.address2,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: formData.country,
        phone: formData.phone,
        license_number: formData.licenseNumber,
        contact_name: formData.contactName,
        contact_title: formData.contactTitle,
        contact_email: formData.contactEmail,
        communication_preferences: formData.commPreferences
      });

      // Redirect to restaurant dashboard
      navigate('/dashboard/restaurant');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || err.message || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="auth-page register-page">
      <div className="auth-container register-container">
        <header className="auth-header">
          <div className="auth-logo-title">
            <img src="/hotf-web-icon.png" alt="Hands On The Fly" className="auth-logo" />
            <h1>Hands On The Fly</h1>
          </div>
        </header>

        <Link to="/" className="auth-back-link">← Back to Home</Link>

        <h2 className="auth-heading">Restaurant Registration</h2>
        <p className="auth-subtitle">Professional Kitchens & Licensed Establishments</p>

        <form className="auth-form register-form" onSubmit={handleSubmit}>
          {error && <div className="auth-error-message">{error}</div>}

          {/* Business Information */}
          <div className="auth-form-group">
            <label htmlFor="businessLogo">Business Logo (Optional)</label>
            <input
              type="file"
              id="businessLogo"
              name="businessLogo"
              accept="image/*"
              onChange={handleFileChange}
              disabled={loading}
              className="register-file-input"
            />
            <p className="register-note">Upload your restaurant logo (JPG, PNG, max 5MB)</p>
          </div>

          <div className="auth-form-group">
            <label htmlFor="businessName">Business Name</label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="establishmentType">Establishment Type</label>
            <select
              id="establishmentType"
              name="establishmentType"
              value={formData.establishmentType}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="">Select Type</option>
              {ESTABLISHMENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Address */}
          <div className="auth-form-group">
            <label htmlFor="address">Street Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="address2">Street Address 2 (Optional)</label>
            <input
              type="text"
              id="address2"
              name="address2"
              value={formData.address2}
              onChange={handleChange}
              placeholder="Apt, Suite, Unit, Building, Floor, etc."
              disabled={loading}
            />
          </div>

          <div className="register-form-row">
            <div className="auth-form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="auth-form-group">
              <label htmlFor="state">State/Province/Region</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="register-form-row">
            <div className="auth-form-group">
              <label htmlFor="postalCode">Postal Code</label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="auth-form-group">
              <label htmlFor="country">Country</label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="auth-form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="licenseNumber">Food Service License Number</label>
            <input
              type="text"
              id="licenseNumber"
              name="licenseNumber"
              value={formData.licenseNumber}
              onChange={handleChange}
              disabled={loading}
            />
            <p className="register-note">Required for US-based restaurants</p>
          </div>

          <div className="auth-form-group">
            <label htmlFor="contactName">Name</label>
            <input
              type="text"
              id="contactName"
              name="contactName"
              value={formData.contactName}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="contactTitle">Title</label>
            <input
              type="text"
              id="contactTitle"
              name="contactTitle"
              value={formData.contactTitle}
              onChange={handleChange}
              placeholder="e.g. Owner, Manager, Chef"
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="contactEmail">Email</label>
            <input
              type="email"
              id="contactEmail"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          {/* Communication Preferences */}
          <div className="auth-form-group">
            <p className="register-comm-header">
              I agree to receive communications from Hands On The Fly via:
            </p>
            <div className="register-checkbox-group">
              <label className="register-checkbox-label-small">
                <input
                  type="checkbox"
                  checked={formData.commPreferences.email}
                  onChange={() => handleCommPrefChange('email')}
                  disabled={loading}
                />
                <span>Email (Optional)</span>
              </label>
              <label className="register-checkbox-label-small">
                <input
                  type="checkbox"
                  checked={formData.commPreferences.sms}
                  onChange={() => handleCommPrefChange('sms')}
                  disabled={loading}
                />
                <span>Text Message (Required)</span>
              </label>
            </div>
            <p className="register-note">Text messaging required to accept shifts</p>
          </div>

          {/* Account Setup */}
          <div className="auth-form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="8"
              disabled={loading}
            />
            <p className="register-note">Minimum 8 characters</p>
          </div>

          <div className="auth-form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength="8"
              disabled={loading}
            />
          </div>

          {/* Terms Acceptance */}
          <div className="register-terms">
            <label className="register-checkbox-label">
              <input
                type="checkbox"
                checked={formData.acceptedTerms}
                onChange={(e) => setFormData(prev => ({ ...prev, acceptedTerms: e.target.checked }))}
                disabled={loading}
              />
              <span>
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank">Privacy Policy</Link>
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="auth-submit-btn auth-submit-btn-restaurant"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Register'}
          </button>
        </form>

        <div className="auth-register-link">
          <p>Already registered?</p>
          <button
            className="auth-register-btn"
            onClick={() => navigate('/restaurant-login')}
            disabled={loading}
          >
            Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestaurantRegister;
